import React from 'react';
import styled from 'styled-components';

import { PersonaResult } from 'api/cms';
import { PublicPage } from 'api/website';
import { Box } from 'components/Box';
import { Button } from 'components/Button';
import Icon from 'components/Icon/Icon';
import { Image } from 'components/Image';
import { CopyClipboardButton, ShareButton } from 'components/SocialButton';
import { Body, BodyMicro, BodySmall, H3 } from 'components/Text';
import { colors } from 'theme';
import { imageUrl } from 'utils/filestack';

const socialEvent = 'Share Persona Quiz Results';

const StyledImage = styled(({ isPage, className, ...props }) => (
  <Box className={className}>
    {/* TODO: Fix this the next time the file is edited. */}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <Image {...props} />
  </Box>
))`
  img {
    display: block;
    margin: 0 auto;
    max-height: 300px;
    max-width: 100%;
    @supports (object-fit: contain) {
      width: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`;

type Props = {
  blockIndex?: number;
  result: PersonaResult;
  content?: PublicPage;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clickRetake?: (...args: Array<any>) => any;
};

export function PersonaQuizBlockResult({
  blockIndex,
  result: { title, description, image, socialMediaText },
  content,
  clickRetake,
}: Props) {
  const message = socialMediaText;
  return (
    <Box
      borderRadius={32}
      data-qa-id="cms-persona-quiz-results"
      overflow="hidden"
      bg={colors.platinum}
    >
      <Box
        px={32}
        py={16}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        backgroundImage={`linear-gradient(
        to left,
        ${colors.skyBlue},
        ${colors.niceBlue}
      )`}
        color="white"
      >
        <BodySmall>{getText('Results')}</BodySmall>

        <Box
          component={Button}
          onClick={clickRetake}
          display="flex"
          alignItems="center"
        >
          <Box mr="4px">
            <Icon
              name="refresh"
              width={16}
              height={16}
              display="block"
              stroke="currentColor"
            />
          </Box>
          <BodySmall>{getText('Retake')}</BodySmall>
        </Box>
      </Box>

      <Box p={32}>
        <Box
          display={['block', null, 'flex']}
          p={[12, null, 32]}
          borderRadius={16}
          bg="white"
        >
          <Box flex="0 0 auto" width={[1, null, 1 / 2]}>
            <H3 data-qa-id="result-title">{title}</H3>
            <Body
              analyticsData={
                blockIndex
                  ? {
                      eventName: 'CMS Block',
                      eventProperties: {
                        block_position: blockIndex,
                        block_type: 'Persona Quiz',
                      },
                    }
                  : {}
              }
              data-qa-id="result-description"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />

            {content && (
              <Box mt={32} mx="-4px" display="flex" flexWrap="wrap">
                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="facebook"
                  utmParams={{}}
                  message={message}
                  m="4px"
                  eventName={socialEvent}
                  eventProperties={{
                    type: 'Facebook',
                  }}
                  bg="#3b5998"
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="facebook" fill="white" width={8} height={15} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Share')}
                    </BodyMicro>
                  </Box>
                </ShareButton>

                <ShareButton
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="twitter"
                  utmParams={{}}
                  message={message}
                  m="4px"
                  eventName={socialEvent}
                  eventProperties={{
                    type: 'Twitter',
                  }}
                  bg="#55acee"
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="twitter" fill="white" width={16} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Tweet')}
                    </BodyMicro>
                  </Box>
                </ShareButton>

                <ShareButton
                  m="4px"
                  object={content}
                  objectType="CMS_PAGE"
                  socialType="linkedin"
                  utmParams={{}}
                  message={message}
                  eventName={socialEvent}
                  eventProperties={{
                    type: 'LinkedIn',
                  }}
                  bg="#777"
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="linkedin" fill="white" width={16} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Share')}
                    </BodyMicro>
                  </Box>
                </ShareButton>

                {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; m: string; object: Publ... Remove this comment to see the full error message */}
                <CopyClipboardButton
                  m="4px"
                  object={content}
                  objectType="CMS_PAGE"
                  data-qa-id="persona-quiz-copy-link"
                  bg={colors.turtleGreen}
                >
                  <Box display="flex" alignItems="center" p="7px">
                    <Icon name="link" fill="white" width={16} />
                    <BodyMicro lineHeight="1" ml="6px">
                      {getText('Copy Link')}
                    </BodyMicro>
                  </Box>
                </CopyClipboardButton>
              </Box>
            )}
          </Box>

          {image && (
            <Box data-qa-id="result-image" flex="1 1 auto" mt={[24, null, 0]}>
              <StyledImage
                src={imageUrl({
                  handle: image.handle,
                  mimetype: image.mimetype,
                })}
                alt={title}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
